/**
 * D56 — the client invoice's totals block: Subtotal, Discount (x%), one row
 * per charge, Grand Total. No VAT row, ever (D53,
 * BUSINESS_RULES#client-invoice-line-vat).
 *
 * Presentation only. The ARITHMETIC (subtotal, discount, charges, grand total) has one owner,
 * `utils/clientInvoiceSummary.ts` (twin of the server's, see mirroredUtilParity.test.js); the page
 * previews it after every edit and the server stores it.
 *
 * `buildInvoiceTotalsRows` turns the already-computed summary into the rows the on-screen totals block and the
 * Export CSV both show, so they cannot disagree; no arithmetic. Its Subtotal-only-with-a-discount-or-charge gate
 * matches the server PDF's (`invoicePdf.service.js` `invoiceTotalRows`); nothing prints from the browser (D98.7).
 */

export interface InvoiceChargeRow {
  label: string;
  amount: number;
}

export type InvoiceTotalsRow =
  | { kind: 'subtotal'; key: string; label: string; amount: number }
  | { kind: 'discount'; key: string; label: string; amount: number }
  | { kind: 'charge'; key: string; label: string; amount: number; index: number }
  | { kind: 'grandTotal'; key: string; label: string; amount: number };

/**
 * D56 point 2 — the printed/displayed rows, read straight off the
 * ALREADY-STORED figures (never recomputed here): Subtotal, Discount (x%)
 * when there is one, one row per charge, GRAND TOTAL. `discountAmount` and
 * `invoiceGrandTotal` are the caller's own `summary.discountAmount` /
 * `summary.invoiceGrandTotal` — this function does not derive them, so it
 * cannot disagree with whatever computed them (the server, or
 * `buildClientInvoiceSummary` for an unsaved preview).
 */
export function buildInvoiceTotalsRows({
  quotedTotal,
  discountPercentage,
  discountAmount,
  charges,
  invoiceGrandTotal,
}: {
  quotedTotal: number | null | undefined;
  discountPercentage: number | null | undefined;
  discountAmount: number | null | undefined;
  charges: ReadonlyArray<InvoiceChargeRow> | null | undefined;
  invoiceGrandTotal: number | null | undefined;
}): InvoiceTotalsRow[] {
  const pct = Number(discountPercentage) || 0;
  const chargeRows = Array.isArray(charges) ? charges : [];
  // Same gate invoicePdf.service.js's buildInvoiceHtml uses — a plain
  // invoice (no discount, no charges) shows no Subtotal row, just the one
  // GRAND TOTAL row it always showed.
  const hasChargesOrDiscount = pct > 0 || chargeRows.length > 0;
  const rows: InvoiceTotalsRow[] = [];

  if (hasChargesOrDiscount) {
    rows.push({ kind: 'subtotal', key: 'subtotal', label: 'Subtotal', amount: Number(quotedTotal) || 0 });
  }
  if (pct > 0) {
    rows.push({
      kind: 'discount',
      key: 'discount',
      label: `Discount (${pct}%)`,
      amount: Number(discountAmount) || 0,
    });
  }
  chargeRows.forEach((c, index) => {
    rows.push({
      kind: 'charge',
      key: `charge-${index}`,
      label: c?.label || 'Charge',
      amount: Number(c?.amount) || 0,
      index,
    });
  });
  rows.push({
    kind: 'grandTotal',
    key: 'grandTotal',
    label: 'GRAND TOTAL',
    amount: Number(invoiceGrandTotal) || 0,
  });

  return rows;
}

/**
 * D56 point 3 — "editable until the invoice is sent". Mirrors
 * `clientInvoice.service.js`'s own `SENT_ONWARD_STATUSES`
 * (`sent`/`partially_paid`/`paid` — neither of the latter two is reachable
 * without first being `sent`) so the page can greet a sent invoice's charges
 * as read-only instead of only finding out from a 409
 * CLIENT_INVOICE_CHARGES_LOCKED after an edit is attempted. The server is
 * still the one that enforces it (`patchClientInvoice`); this is the client
 * reading the same three strings, not a second copy of the rule's REASON —
 * there is no CommonJS/browser bridge to import the server's own `Set`
 * across, the same constraint `chargesFromDoc`/`chargeRowsFromDoc`
 * (`src/utils/pricing.js`) note for why they have no client twin.
 */
const SENT_ONWARD_STATUSES = new Set(['sent', 'partially_paid', 'paid']);

export function isInvoiceSentOnward(status: string | null | undefined): boolean {
  return SENT_ONWARD_STATUSES.has(String(status || ''));
}

/**
 * D56 point 3 — "Additional invoices bill at full price, with no discount
 * and no charges" is a RULE (`clientInvoice.service.js`'s
 * `patchClientInvoice`, `CLIENT_INVOICE_CHARGES_NOT_ON_ADDITIONAL`), not just
 * a generation default. One owner for "may this invoice's charges be edited
 * right now" — both the totals block's editable/read-only switch
 * (InvoiceTotalsBlock) and the PATCH payload builder
 * (buildInvoicePatchPayload) call this rather than each spelling out
 * `!isInvoiceSentOnward(...) && !isAdditional` on its own, which is exactly
 * the shape that drifts (one call site fixed, the other forgotten).
 */
export function canEditInvoiceCharges(invoice: {
  status?: string | null;
  isAdditional?: boolean | null;
}): boolean {
  return !isInvoiceSentOnward(invoice.status) && invoice.status !== 'cancelled' && !invoice.isAdditional;
}
